import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';
import { createJSONStorage, type StateStorage } from 'zustand/middleware';

export const AUTH_STORAGE_KEY = 'user-storage';
const AUTH_TOKEN_KEY = 'auth-token';

export type AuthPersistedState = {
  user: unknown;
  token: string | null;
  isAuthenticated: boolean;
};

const authWebStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(name);
  },
};

async function syncTokenToSecureStore(value: string) {
  if (Platform.OS === 'web') return;

  try {
    const parsed = JSON.parse(value) as { state?: AuthPersistedState };
    const token = parsed?.state?.token;
    if (token) {
      await SecureStore.setItemAsync(AUTH_TOKEN_KEY, token);
    } else {
      await SecureStore.deleteItemAsync(AUTH_TOKEN_KEY);
    }
  } catch {
    // ignore malformed payloads
  }
}

const authNativeStorage: StateStorage = {
  getItem: async (name) => {
    const value = await AsyncStorage.getItem(name);
    const secureToken = await SecureStore.getItemAsync(AUTH_TOKEN_KEY);

    if (!secureToken) {
      return value;
    }

    if (!value) {
      return JSON.stringify({
        state: { user: null, token: secureToken, isAuthenticated: true },
        version: 0,
      });
    }

    try {
      const parsed = JSON.parse(value) as { state?: Partial<AuthPersistedState>; version?: number };
      const state: AuthPersistedState = {
        user: parsed.state?.user ?? null,
        token: parsed.state?.token ?? secureToken,
        isAuthenticated: parsed.state?.isAuthenticated ?? true,
      };
      if (!parsed.state?.token) {
        return JSON.stringify({ ...parsed, state });
      }
    } catch {
      return value;
    }

    return value;
  },
  setItem: async (name, value) => {
    await AsyncStorage.setItem(name, value);
    await syncTokenToSecureStore(value);
  },
  removeItem: async (name) => {
    await AsyncStorage.removeItem(name);
    if (Platform.OS !== 'web') {
      await SecureStore.deleteItemAsync(AUTH_TOKEN_KEY);
    }
  },
};

export const authPersistStorage =
  Platform.OS === 'web'
    ? createJSONStorage<AuthPersistedState>(() => authWebStorage)
    : createJSONStorage<AuthPersistedState>(() => authNativeStorage);

/** Garante gravação síncrona antes de navegar ou ir para background. */
export async function flushAuthPersist(state: AuthPersistedState) {
  const payload = JSON.stringify({ state, version: 0 });

  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AUTH_STORAGE_KEY, payload);
    }
    return;
  }

  await AsyncStorage.setItem(AUTH_STORAGE_KEY, payload);
  await syncTokenToSecureStore(payload);
}
